import { gql } from '__generated__';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EditFeaturesInput, Gender,
  GetFeaturesForProjectQuery as GetFeaturesForProject,
} from '__generated__/graphql';

import { GetSessionQuery } from '../Auth/SessionProvider';
import { WithRefetch } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';

gql(`
  fragment FeatureFields on Project {
    id
    genders
    enable_abandoned_cart_reminders
    enable_assignment_reminders
    enable_reviews
    enable_review_requests
  }
`);

export const EditFeaturesMutation = gql(`
  mutation EditFeatures($input: EditFeaturesInput!) {
    editFeatures(input: $input) {
      ...FeatureFields
    }
  }
`);

export const GetFeaturesForProjectQuery = gql(`
  query GetFeaturesForProject($id: ID!) {
    project(id: $id) {
      ...FeatureFields
    }
  }
`);

interface EditFeaturesPageProps {
  projectId: string;
}

const EditFeaturesPage = ({ projectId, refetchQueries = [GetSessionQuery] }: EditFeaturesPageProps & WithRefetch) => {
  const { data } = useQuery(GetFeaturesForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return null;
  }

  return <EditFeaturesView project={project} refetchQueries={refetchQueries} />;
};

const EditFeaturesView = ({ project, refetchQueries }: GetFeaturesForProject & WithRefetch) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const genderNeutralEnabled = project.genders.includes(Gender.neutral);
  const abandonedCartRemindersEnabled = project.enable_abandoned_cart_reminders;
  const assignmentRemindersEnabled = project.enable_assignment_reminders;
  const reviewsEnabled = project.enable_reviews;
  const reviewRequestsEnabled = project.enable_review_requests;
  const reviewRequestsBeforeDisableRef = useRef<boolean | null>(null);

  const [editFeatures, { error, loading }] = useMutation(
    EditFeaturesMutation, {
      refetchQueries,
      onCompleted: () => {
        notifier.success(t('notifications.features_saved'));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const toggle = (option: keyof Omit<EditFeaturesInput, 'project'>, current: boolean) => {
    editFeatures({
      variables: {
        input: {
          project: {
            id: project.id,
          },
          [option]: !current,
        },
      },
    });
  };

  const toggleReviews = () => {
    if (reviewsEnabled) {
      reviewRequestsBeforeDisableRef.current = reviewRequestsEnabled;

      editFeatures({
        variables: {
          input: {
            project: {
              id: project.id,
            },
            enable_reviews: false,
            enable_review_requests: false,
          },
        },
      });

      return;
    }

    const restoredReviewRequests = reviewRequestsBeforeDisableRef.current ?? reviewRequestsEnabled;
    reviewRequestsBeforeDisableRef.current = null;

    editFeatures({
      variables: {
        input: {
          project: {
            id: project.id,
          },
          enable_reviews: true,
          enable_review_requests: restoredReviewRequests,
        },
      },
    });
  };

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H3>
            {t('features')}
          </UI.H3>
          <UI.InputDescription sc={{ mt: 1 }}>
            {t('features_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Form onSubmit={() => editFeatures()}>
          <UI.Card>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !genderNeutralEnabled, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureGenderNeutral">
                    {t('gender_neutral_feature')}
                  </UI.InputLabel>
                  {t('gender_neutral_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureGenderNeutral"
                    checked={genderNeutralEnabled}
                    onChange={() => toggle('gender_neutral', genderNeutralEnabled)}
                    disabled={loading}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !abandonedCartRemindersEnabled, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureAbandonedCartReminders">
                    {t('abandoned_cart_reminders')}
                  </UI.InputLabel>
                  {t('abandoned_cart_reminders_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureAbandonedCartReminders"
                    checked={abandonedCartRemindersEnabled}
                    onChange={() => toggle('enable_abandoned_cart_reminders', abandonedCartRemindersEnabled)}
                    disabled={loading}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !assignmentRemindersEnabled, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureAssignmentReminders">
                    {t('assignment_reminders')}
                  </UI.InputLabel>
                  {t('assignment_reminders_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureAssignmentReminders"
                    checked={assignmentRemindersEnabled}
                    onChange={() => toggle('enable_assignment_reminders', assignmentRemindersEnabled)}
                    disabled={loading}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !reviewsEnabled, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureReviews">
                    {t('enable_reviews')}
                  </UI.InputLabel>
                  {t('enable_reviews_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureReviews"
                    checked={reviewsEnabled}
                    disabled={loading}
                    onChange={() => toggleReviews()}
                  />
                </UI.Div>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '2fr 1fr', alignVertical: 'center' }}>
                <UI.Div sc={{ muted: !reviewsEnabled || !reviewRequestsEnabled, pt: 0.25 }}>
                  <UI.InputLabel sc={{ mb: 0 }} htmlFor="FeatureReviewRequests">
                    {t('enable_review_requests')}
                  </UI.InputLabel>
                  {t('enable_review_requests_description')}
                </UI.Div>

                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Toggle
                    id="FeatureReviewRequests"
                    checked={reviewsEnabled && reviewRequestsEnabled}
                    disabled={!reviewsEnabled || loading}
                    onChange={() => toggle('enable_review_requests', reviewRequestsEnabled)}
                  />
                </UI.Div>
              </UI.FormGrid>
            </UI.FormGrid>
          </UI.Card>
        </UI.Form>
      </UI.GridContainer>
    </UI.Container>
  );
};

export default EditFeaturesPage;
